/**
 * Транспорт модуля: ОДИН HTTP-клиент на оба режима (`@aseller/api-client`).
 *
 * - **standalone** — Bearer собственного JWT модуля, 401 → refresh + один повтор,
 *   заголовок `X-Platform-Token` навешивает слой `@aseller/platform-token`;
 * - **embed** — личность даёт платформа: Bearer из `context.getToken()` и
 *   `X-Platform-Api-Base` из `context.backendApiUrl` ставит сам `createHostApi`,
 *   а токенный слой там полностью no-op.
 *
 * ⚠️ `setHostContext` ПЕРЕСОЗДАЁТ axios-инстанс (`host-contract/dist/dual-mode.js:28-46`):
 * `client` — это Proxy поверх подменяемого `current`, и всё, что было поставлено на
 * прошлый инстанс (интерсепторы, `withCredentials`, таймаут), умирает вместе с ним.
 * Поэтому единственная дорога к смене режима — `applyHostContext`, которая после
 * подмены ставит модульный обвес заново. Первичная установка standalone идёт тем же
 * путём с `null`, чтобы кодовая дорога была одна, а не две расходящиеся.
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { createApi, toApiError, type Api } from '@aseller/api-client';
import {
  createPlatformTokenStore,
  installPlatformTokenInterceptors,
  type PlatformTokenStore,
} from '@aseller/platform-token';
import { LOG_TAG } from '../lib/logTag';
import { useAuthStore } from '../session/authStore';
import { API_URL } from './apiUrl';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    /**
     * Номер жизни модуля, в которой запрос УШЁЛ в сеть (см. `moduleEpoch`).
     *
     * Аугментация, а не локальный интерфейс с кастом: поле читают три разных
     * места (штамп, повтор 401, повтор 503), и каждое привело бы к своему касту
     * `error.config` — а расходящиеся касты одного и того же поля рано или
     * поздно разъезжаются. Ту же технику для своего флага повтора применяет
     * `@aseller/platform-token` (`dist/interceptors.d.ts`).
     */
    _moduleEpoch?: number;
  }
}

// Таймаут задан ЯВНО: пакет подставляет свои 30 с там, где у прежнего клиента
// таймаута не было вовсе. Смена поведения должна быть решением, а не побочным
// эффектом переезда. Ставится и на host-инстанс: `createHostApi` опцию таймаута
// не принимает, и без этого embed остался бы без ограничения вовсе.
const MODULE_TIMEOUT_MS = 30_000;

// Прокси обмена refresh→access. Константа объявлена выше списков намеренно: она
// участвует в одном из них, и литерал в двух местах разъехался бы молча.
export const PLATFORM_EXCHANGE_URL = '/api/platform/token';

// ⚠️ ДВА списка, а не один. Вопросы у потребителей разные, и один ответ на оба
// УЖЕ дал дефект (найден на приёмке S2): пути совпадали, пока обмен платформенного
// токена был открытым, и разошлись, как только задача 2 закрыла его модульным
// гардом. Совпадение четырёх записей ниже — свойство сегодняшних роутов, а не
// правило. **Не сливать обратно в один список.**

// Вопрос 1: на каких путях НЕ работает платформенно-токенный слой — не вешать
// `X-Platform-Token`, не разбирать 428 и не переобменивать платформенный refresh.
// `/api/platform/token` здесь обязателен, и по одной причине: любое из этих
// действий на самом обмене — рекурсия того же обмена. Потому путь и в дефолте
// пакета. Дефолт при этом не годится целиком: `['/api/auth/', '/api/platform/token']`
// не покрывает наш админ-канал `/api/admin/*`.
export const PLATFORM_TOKEN_EXEMPT_URLS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/admin/login',
  '/api/admin/check',
  PLATFORM_EXCHANGE_URL,
] as const;

// Вопрос 2: на чьём 401 НЕ пытаться обновить МОДУЛЬНЫЙ токен. Здесь 401 — штатный
// ответ канала (неверный пароль, мёртвый refresh), и ретрай либо зациклится, либо
// разлогинит пользователя из-за опечатки в пароле. Обновление модульного токена
// рекурсии на обмене не создаёт: оно ходит по своему, другому роуту.
//
// `/api/platform/token` в этот список НЕ входит — и остаётся вне его после того,
// как гард `Depends(get_current_user)` с обмена снят (S2b, задача 2). Сегодня
// единственный 401 этого роута — `refresh_token_invalid`, то есть мёртвый
// ПЛАТФОРМЕННЫЙ refresh; попытка обновить по нему модульный токен стоит одного
// лишнего запроса и исхода не меняет (повторный обмен отвечает тем же 401, и
// стор честно уходит в `expired`). Списки при этом остаются РАЗНЫМИ: они отвечают
// на разные вопросы, и слитые обратно в один уже давали дефект.
// Рекурсии нет: `refreshAccessToken` ходит ГОЛЫМ axios мимо интерсепторов, а
// повтор ограничен `_authRetried` — худший случай один лишний запрос.
export const MODULE_REFRESH_EXEMPT_URLS = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/admin/login',
  '/api/admin/check',
] as const;

// Подстрока, а не префикс: тем же правилом сверяет свой список токенный слой
// (`interceptors.ts`, `url.includes(fragment)`), и одно правило матчинга на оба
// списка не даёт потребителям разойтись ещё и в понимании «путь совпал».
// `PLATFORM_TOKEN_EXEMPT_URLS` своего предиката не имеет: его единственный
// потребитель — сам пакет, и он сверяет список у себя.
function isModuleRefreshExempt(url: string): boolean {
  return MODULE_REFRESH_EXEMPT_URLS.some((exempt) => url.includes(exempt));
}

// Режим читается НА КАЖДЫЙ запрос, а не снимается при установке интерсепторов:
// один и тот же инстанс живёт от mount до unmount и меняет режим под собой.
let hostBound = false;

/** true, пока контекст платформы не привязан: только здесь работает токенный слой. */
export function isStandaloneMode(): boolean {
  return !hostBound;
}

const api: Api = createApi({
  baseURL: API_URL,
  // Только собственный JWT модуля: в embed эту функцию не зовут вовсе — там
  // личность даёт платформа через `context.getToken()`, и её токен приоритетнее
  // локального (standalone-токен мог остаться в localStorage от прошлой сессии
  // в этом же браузере и увёл бы запросы под чужого пользователя).
  getStandaloneToken: () => useAuthStore.getState().accessToken,
  // Намеренный no-op с логом, а НЕ resetAuth(): инстанс пакета несёт свой
  // response-интерсептор, axios зовёт хэндлеры в порядке регистрации, и пакетный
  // срабатывает раньше наших. resetAuth() опустошил бы стор до того, как наш
  // интерсептор попробует продлить сессию, — тихое продление умерло бы молча.
  // Владелец 401 в standalone — installAuthRetry ниже.
  onStandaloneUnauthorized: () => {
    console.debug(`${LOG_TAG} 401 в standalone: продлением сессии занят интерсептор модуля`);
  },
  // Пакетный хэндлер зовёт `onStandaloneUnauthorized` только вне auth-каналов, то
  // есть отвечает на ТОТ ЖЕ вопрос, что и наш ретрай: «этот 401 — про сессию?».
  isAuthRequest: isModuleRefreshExempt,
  timeout: MODULE_TIMEOUT_MS,
});

/** Типизированные обёртки: возвращают data, бросают `ApiError` (status + url). */
export const typedApi: Api = api;

/** axios-инстанс (escape hatch): нужен для params, blob и прочего нестандартного. */
export const apiClient: AxiosInstance = api.client;

interface PlatformTokenResponse {
  accessToken: string;
}

/**
 * Отказ обмена → ошибка, по которой стор пакета примет ВЕРНОЕ решение.
 *
 * Ветки «а этот 401 не про платформенный токен» здесь БОЛЬШЕ НЕТ, и её отсутствие
 * намеренное. Она существовала, пока `POST /api/platform/token` был закрыт
 * `Depends(get_current_user)`: тогда на роуте жили два несовместимых 401 —
 * `refresh_token_invalid` платформы и «нет модульной сессии» нашего гарда, и
 * второй, приняв его за первый, стирал бы вставленный пользователем токен.
 * Гард снят (решение заказчика 2026-08-20: платформенный токен — ОСНОВНОЙ вход,
 * учётные данные здесь сам refresh, и проверяет его платформа), других
 * продюсеров 401 у роута нет — значит любой его 401 означает ровно мёртвый
 * refresh, и `ApiError(401)` для стора здесь единственный честный ответ.
 * Возвращать гард — значит возвращать и разведение: одно без другого лжёт.
 */
function asExchangeFailure(error: unknown): unknown {
  // Не отказ сервера, а «модуль размонтировали»: обёртка стёрла бы это различие.
  if (error instanceof RetryAbortedError) return error;
  return toApiError(error, PLATFORM_EXCHANGE_URL);
}

/**
 * Хранилище платформенного токена standalone-пользователя.
 *
 * Обмен идёт через axios-инстанс модуля (а не «голым» axios): роут открыт, но
 * ходить мимо инстанса значило бы потерять базовый URL режима, `withCredentials`
 * и таймаут — то есть завести второй, расходящийся транспорт.
 * Нормализация ответа — своя, а не типизированной обёрткой `api.post`: `api.post`
 * оборачивает любой отказ в `toApiError` до нашего кода, а `RetryAbortedError`
 * обязан дойти до стора неизменным — см. `asExchangeFailure`.
 */
export const platformTokenStore = createPlatformTokenStore({
  storageKeyPrefix: 'idea_calc_platform',
  exchange: async (refreshToken: string): Promise<string> => {
    let data: PlatformTokenResponse;
    try {
      const response = await apiClient.post<PlatformTokenResponse>(PLATFORM_EXCHANGE_URL, {
        refreshToken,
      });
      data = response.data;
    } catch (error) {
      throw asExchangeFailure(error);
    }
    if (!data?.accessToken) {
      throw new Error('platform token exchange: пустой accessToken в ответе');
    }
    return data.accessToken;
  },
});

interface RetryableAuthConfig extends InternalAxiosRequestConfig {
  _authRetried?: boolean;
}

// Дедупликация: параллельные 401 не должны порождать N запросов на refresh.
let refreshPromise: Promise<boolean> | null = null;

// Номер жизни модуля: растёт на каждом disposeApiState(), то есть на каждом
// unmount ремоута. Продолжение прошлой жизни не имеет права ни писать токены в
// стор (там уже пусто или уже другой пользователь), ни отправлять свой запрос:
// Bearer в этот момент чужой. Счётчик, а не булев флаг «отменено»: флаг
// пришлось бы снимать на следующем mount'е, а mount про HTTP-слой ничего не
// знает — новая жизнь просто получает новый номер, и отмена не залипает.
//
// Носитель номера — САМ КОНФИГ запроса (`_moduleEpoch`, штампует
// `installEpochStamp`), а не снимок в замыкании обработчика. Снимок отвечает
// только на вопрос «сменилась ли жизнь, пока мы ждали», и слеп ровно к тому
// случаю, который портит данные: ответ 503/401, ПРИШЕДШИЙ после unmount. К
// моменту разбора такого ответа `moduleEpoch` уже новый, снимать его там
// бессмысленно — сверять надо со штампом отправки.
let moduleEpoch = 0;

/** Что именно снял unmount — попадает в текст RetryAbortedError. */
const ABORTED = {
  retry503: '503 retry',
  authRetry: 'auth retry',
} as const;

type AbortedOperation = (typeof ABORTED)[keyof typeof ABORTED];

/**
 * Отказ повтора, который модуль не имеет права доводить до конца: размонтирование
 * случилось раньше, чем истёк таймер паузы 503 или вернулся ответ refresh'а.
 *
 * Именно ошибка, а не тихий `false`: вызывающий обязан отличать «мы ушли со
 * страницы» от «сервер отказал», иначе отмена выглядит как провал сохранения.
 */
export class RetryAbortedError extends Error {
  constructor(operation: AbortedOperation) {
    super(`${operation} aborted: the module was unmounted`);
    this.name = 'RetryAbortedError';
  }
}

interface RefreshTokensResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * Штамп жизни модуля на исходящем конфиге.
 *
 * `??=`, а не присваивание: повтор идёт `client.request(config)` ТЕМ ЖЕ
 * конфигом и обязан остаться в своей эпохе, а не перештамповываться текущей —
 * иначе штамп сообщал бы «когда ушла последняя попытка», а нужен «в какой жизни
 * модуля запрос начался».
 *
 * Регистрируется ПОСЛЕДНИМ намеренно: axios зовёт request-интерсепторы в
 * обратном порядке регистрации (`legacyInterceptorReqResOrdering` в дефолтах
 * transitional, `axios/lib/core/Axios.js`), то есть последний зарегистрированный
 * отрабатывает первым — до `await` ленивого обмена платформенного токена. Штамп
 * обязан быть снят ДО любого ожидания внутри цепочки, иначе запрос, начатый в
 * жизни N, уехал бы помеченным N+1 и повторился бы под следующим пользователем.
 */
function installEpochStamp(client: AxiosInstance): void {
  client.interceptors.request.use((config) => {
    config._moduleEpoch ??= moduleEpoch;
    return config;
  });
}

/**
 * Запрос ушёл в текущей жизни модуля — значит его повтор попадёт к тому же
 * пользователю, чей Bearer стоял на первой попытке.
 *
 * Незаштампованных конфигов здесь не бывает: штамп и оба повторяющих
 * интерсептора ставит одна и та же `applyHostContext` на один и тот же инстанс,
 * и до response-интерсепторов доходит только то, что прошло request-цепочку.
 * `undefined !== moduleEpoch` поэтому не «на всякий случай», а честный отказ
 * повторять запрос неизвестного происхождения.
 */
function isFromCurrentEpoch(config: InternalAxiosRequestConfig): boolean {
  return config._moduleEpoch === moduleEpoch;
}

/**
 * Отказ продления — про апстрим, а не про личность?
 *
 * 401/403/422 от `/api/auth/refresh` означают мёртвый refresh: сессии конец, и
 * состояние надо чистить. Всё остальное (5xx, сеть, таймаут) — временная авария,
 * после которой та же пара токенов ещё будет рабочей.
 */
function isUpstreamFailure(error: unknown): boolean {
  const status = (error as AxiosError | undefined)?.response?.status;
  return status === undefined || status >= 500;
}

async function refreshAccessToken(epoch: number): Promise<boolean> {
  const { refreshToken } = useAuthStore.getState();
  if (!refreshToken) return false;

  let tokens: RefreshTokensResponse;
  try {
    // Голый axios (без интерсепторов apiClient), чтобы refresh не зациклился.
    const { data } = await axios.post<RefreshTokensResponse>(
      `${API_URL}/api/auth/refresh`,
      { refresh_token: refreshToken },
      { withCredentials: true },
    );
    tokens = data;
  } catch (error) {
    // Разлогинивать после unmount некого: в сторе либо пусто, либо уже
    // следующий пользователь, и resetAuth() выкинул бы из сессии его.
    if (epoch !== moduleEpoch) throw new RetryAbortedError(ABORTED.authRetry);
    // Авария — не отказ в личности. У сессии, вошедшей через платформу, продление
    // ходит в платформу за свежей ролью, и её 502 означает «повтори позже», а не
    // «этот человек больше не он». Разлогинивать по чужой аварии — значит выкидывать
    // пользователя из работы всякий раз, когда у платформы дрогнул Redis; тот же
    // инвариант 401 ≠ 502 бэкенд держит у себя (backend/auth/validate_gateway.py).
    if (isUpstreamFailure(error)) return false;
    useAuthStore.getState().resetAuth();
    return false;
  }

  if (epoch !== moduleEpoch) throw new RetryAbortedError(ABORTED.authRetry);
  useAuthStore.getState().applyTokens(tokens.access_token, tokens.refresh_token);
  return true;
}

/**
 * 401 в standalone (CU-869dt3dz6) → refresh собственного HS256-токена модуля и
 * ровно один повтор запроса. Второй 401 (`config._authRetried`) — настоящий отказ.
 *
 * В MF-embed интерсептор не вмешивается: там 401 обрабатывает `createHostApi`
 * (обмен через `context.onUnauthorized()` + свой единственный повтор), и вторая
 * попытка продления с нашей стороны сожгла бы бюджет повтора впустую.
 */
function installAuthRetry(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableAuthConfig | undefined;
      const url: string = config?.url ?? '';

      if (
        !isStandaloneMode() ||
        error.response?.status !== 401 ||
        !config ||
        config._authRetried ||
        isModuleRefreshExempt(url)
      ) {
        return Promise.reject(error);
      }

      // 401 мог прийти уже ПОСЛЕ unmount. Продлевать по нему сессию нельзя
      // (refresh в сторе — уже следующего пользователя), повторять запрос —
      // тем более: он уехал бы с его Bearer'ом.
      if (!isFromCurrentEpoch(config)) {
        return Promise.reject(new RetryAbortedError(ABORTED.authRetry));
      }

      const { refreshToken } = useAuthStore.getState();

      // Снимок эпохи до первого await: пока мы ждём новый access, модуль могли
      // размонтировать. Тогда повторять запрос нельзя — он ушёл бы уже из чужой
      // сессии и с чужим Bearer'ом.
      const epoch = moduleEpoch;

      if (refreshToken) {
        config._authRetried = true;
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken(epoch).finally(() => {
            refreshPromise = null;
          });
        }
        const refreshed = await refreshPromise;
        if (epoch !== moduleEpoch) {
          return Promise.reject(new RetryAbortedError(ABORTED.authRetry));
        }
        if (refreshed) {
          config.headers['Authorization'] = `Bearer ${useAuthStore.getState().accessToken}`;
          return client.request(config);
        }
      }

      return Promise.reject(error);
    },
  );
}

/**
 * CU-869dnpp43: при 503 (БД временно недоступна, save verification failed)
 * автоматически повторяем запрос до 2 раз, уважая Retry-After header.
 * Без этого пользователь видел бы провал и должен был сам нажимать кнопку.
 */
const MAX_RETRIES_ON_503 = 2;
const MAX_RETRY_AFTER_SECONDS = 30;

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
}

interface PendingRetry {
  timerId: number;
  abort: (reason: RetryAbortedError) => void;
}

// Пауза до 30 с переживает unmount: без учёта таймеров повтор ушёл бы уже под
// следующим пользователем, с его токеном и по его сессии.
const pendingRetries = new Set<PendingRetry>();

function waitBeforeRetry(seconds: number): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const entry: PendingRetry = {
      timerId: window.setTimeout(() => {
        pendingRetries.delete(entry);
        resolve();
      }, seconds * 1000),
      abort: reject,
    };
    pendingRetries.add(entry);
  });
}

function installRetryOn503(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableConfig | undefined;
      if (!config || error.response?.status !== 503) return Promise.reject(error);

      // Ответ 503 мог прийти уже ПОСЛЕ unmount. Снятие таймеров в
      // disposeApiState() этот случай не закрывает: оно отменяет повторы, уже
      // СТОЯВШИЕ в очереди, а опоздавший 503 заводит в ней новую запись и через
      // паузу уходит `client.request(config)` — то есть на инстанс следующего
      // пользователя, с его Bearer'ом и его сессией.
      if (!isFromCurrentEpoch(config)) {
        return Promise.reject(new RetryAbortedError(ABORTED.retry503));
      }

      config._retryCount = (config._retryCount ?? 0) + 1;
      if (config._retryCount > MAX_RETRIES_ON_503) return Promise.reject(error);

      const retryAfterHeader = error.response.headers['retry-after'];
      const retryAfterSec = Math.min(
        MAX_RETRY_AFTER_SECONDS,
        Math.max(1, parseInt(String(retryAfterHeader ?? '1'), 10) || 1),
      );

      await waitBeforeRetry(retryAfterSec);
      return client.request(config);
    },
  );
}

/**
 * Тот же стор, но для ИНТЕРСЕПТОРОВ: сбой обмена не отклоняет чужой запрос.
 *
 * Request-интерсептор пакета зовёт `getFreshAccessToken()` без `try/catch`
 * (`platform-token/dist/interceptors.js:33-43`), а стор отдаёт `null` только на
 * `ApiError` со статусом 401 — любой другой отказ он бросает наружу
 * (`dist/store.js:105-115`). Наш обмен даёт `ApiError(502)` на недоступной
 * платформе, `ApiError(0)` на сетевом сбое и таймауте; каждый из них
 * отклонил бы не обмен, а запрос, который всего лишь хотел добавить
 * НЕОБЯЗАТЕЛЬНЫЙ заголовок. Падение платформы делало бы неработоспособным весь
 * модуль — сохранение расчёта, Keepa, экспорт, — хотя собственному бэкенду
 * модуля платформенный токен не нужен ни на одном роуте.
 *
 * Это не молчаливый фолбэк: отказ пишется в консоль явной строкой, а статус
 * стора (`missing` / `expired`) и текст в модале остаются каналом, где
 * пользователь видит проблему по делу. `RetryAbortedError` пробрасывается как
 * есть — «модуль размонтировали» обязано оставаться отличимым от «обмен не
 * удался», иначе запрос уехал бы уже из чужой сессии.
 *
 * Делегат через прототип, а НЕ подмена метода на самом сторе: UI зовёт
 * `getFreshAccessToken()` напрямую (`PlatformTokenModal`), и там отказ обмена —
 * единственный способ сказать пользователю, что вставленный токен не принят.
 */
const interceptorTokenStore: PlatformTokenStore = Object.create(platformTokenStore, {
  getFreshAccessToken: {
    value: async (): Promise<string | null> => {
      try {
        return await platformTokenStore.getFreshAccessToken();
      } catch (error) {
        if (error instanceof RetryAbortedError) throw error;
        console.warn(
          `${LOG_TAG} обмен платформенного токена не удался — запрос уходит без X-Platform-Token:`,
          error,
        );
        return null;
      }
    },
  },
});

function installPlatformTokenLayer(client: AxiosInstance): void {
  installPlatformTokenInterceptors(client, {
    store: interceptorTokenStore,
    isStandalone: isStandaloneMode,
    exemptPathFragments: PLATFORM_TOKEN_EXEMPT_URLS,
  });
}

// Порядок регистрации = порядок вызова response-хэндлеров: сначала пакетный
// (он живёт на инстансе с рождения), затем наш 401, затем 503, затем токенный
// слой с его 428 и записью 502-детали. Ветки не пересекаются по статусам.
//
// Штамп эпохи — request-интерсептор, и для НЕГО порядок обратный: он идёт
// последним в списке именно затем, чтобы отработать первым в request-цепочке
// (см. `installEpochStamp`).
const INTERCEPTOR_INSTALLERS: readonly ((client: AxiosInstance) => void)[] = [
  installAuthRetry,
  installRetryOn503,
  installPlatformTokenLayer,
  installEpochStamp,
];

/** `HostContext | null` — без прямого импорта контракта: тип берётся из его же API. */
type HostContextBinding = Parameters<Api['setHostContext']>[0];

/**
 * Единственная дорога к смене режима.
 *
 * `setHostContext` подменяет axios-инстанс целиком, поэтому сразу за подменой
 * возвращается весь модульный обвес: `withCredentials` (cookie `admin_token`
 * авторизует — `backend/auth/dependencies.py`), явный таймаут и все три
 * интерсепторных слоя. Знание про пересоздание инстанса живёт ТОЛЬКО здесь.
 */
export function applyHostContext(context: HostContextBinding): void {
  hostBound = context !== null;
  api.setHostContext(context);
  apiClient.defaults.withCredentials = true;
  apiClient.defaults.timeout = MODULE_TIMEOUT_MS;
  for (const install of INTERCEPTOR_INSTALLERS) install(apiClient);
}

/**
 * Сброс состояния HTTP-слоя, живущего в модульной области.
 *
 * Зовётся из disposeModuleState(). Сам axios-инстанс здесь НЕ пересоздаётся:
 * отвязку контекста делает `applyHostContext(null)` из `mountRemote.unmount`, а
 * интерсепторы читают токен из стора в момент запроса, поэтому очищенного стора
 * достаточно, чтобы под следующим пользователем не ушёл чужой Bearer. Здесь
 * снимается то, что переживает unmount само по себе:
 *
 * - право уже летящих refresh-запросов дописать свой результат: смена эпохи
 *   заставляет их продолжение отклониться RetryAbortedError вместо записи
 *   токенов предыдущего пользователя в стор и в его localStorage-персист.
 *   Обнулять хэндлы для этого недостаточно — оно снимает лишь дедупликацию,
 *   а не сам летящий запрос;
 * - дедуплицирующий промис refresh'а: без обнуления следующий mount переиспользовал
 *   бы промис прошлой эпохи и получил бы отмену на собственном, законном refresh'е;
 * - отложенные повторы 503: таймер до 30 с иначе выстрелит уже после mount'а
 *   под другой личностью. Ожидающий промис не бросается висеть — он падает
 *   RetryAbortedError, чтобы вызывающий код увидел явный отказ, а не зависание.
 *
 * Повторы, которых на момент вызова ещё НЕ существует (ответ 503 или 401,
 * пришедший после unmount), здесь не снять — их закрывает штамп эпохи на
 * конфиге: см. `installEpochStamp` и сверки в обоих повторяющих интерсепторах.
 */
export function disposeApiState(): void {
  moduleEpoch += 1;
  refreshPromise = null;
  for (const entry of pendingRetries) {
    window.clearTimeout(entry.timerId);
    entry.abort(new RetryAbortedError(ABORTED.retry503));
  }
  pendingRetries.clear();
}

// Первичная привязка: standalone идёт той же дорогой, что и embed, — иначе
// поведение первого монтирования разошлось бы с поведением всех последующих.
applyHostContext(null);
